/**
 * SummonSystem — manages player-controlled summon entities.
 *
 * Handles spawning, HP tracking, death, commands (converge, detonate),
 * and archetype-based querying for the Conjuration tree (wisps, companions,
 * shades, chimera) and Specter's phantom decoys.
 *
 * Pure logic — no Phaser dependencies. Rendering and pathfinding are
 * wired in the integration phase.
 *
 * @see ADR-064 (attunement shared systems architecture)
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SummonArchetype = 'companion' | 'wisp' | 'shade' | 'decoy';

export interface SummonConfig {
  readonly id: string;
  readonly archetype: SummonArchetype;
  readonly baseHp: number;
  readonly baseDamage: number;
  readonly baseSpeed: number;
  /** Max distance from player before returning (tiles). */
  readonly leashRadiusTiles: number;
  /** Duration in ms. 0 or undefined = permanent until killed. */
  readonly durationMs?: number;
  /** How much enemy aggro this summon draws (0–1). */
  readonly aggroWeight: number;
}

export interface SummonInstance {
  readonly instanceId: string;
  readonly config: SummonConfig;
  hp: number;
  readonly maxHp: number;
  readonly isDead: boolean;
  x: number;
  y: number;
  /** Time alive in ms. */
  elapsedMs: number;
}

/** Emitted when something happens to a summon. */
export interface SummonEvent {
  readonly type: 'spawned' | 'died' | 'expired' | 'detonated' | 'destroyed';
  readonly instanceId: string;
  readonly config: SummonConfig;
  readonly x: number;
  readonly y: number;
}

/** Detonate result for damage application by the caller. */
export interface DetonateResult {
  readonly instanceId: string;
  readonly x: number;
  readonly y: number;
  readonly damage: number;
  readonly radiusTiles: number;
}

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

class SummonState implements SummonInstance {
  readonly instanceId: string;
  readonly config: SummonConfig;
  readonly maxHp: number;
  hp: number;
  x: number;
  y: number;
  elapsedMs = 0;
  private _dead = false;

  constructor(instanceId: string, config: SummonConfig, x: number, y: number) {
    this.instanceId = instanceId;
    this.config = config;
    this.maxHp = config.baseHp;
    this.hp = config.baseHp;
    this.x = x;
    this.y = y;
  }

  get isDead(): boolean {
    return this._dead;
  }

  takeDamage(amount: number): void {
    if (this._dead) return;
    this.hp = Math.max(0, this.hp - amount);
    if (this.hp === 0) {
      this._dead = true;
    }
  }

  kill(): void {
    this.hp = 0;
    this._dead = true;
  }
}

// ---------------------------------------------------------------------------
// System
// ---------------------------------------------------------------------------

export class SummonSystem {
  private readonly summons = new Map<string, SummonState>();
  private readonly events: SummonEvent[] = [];
  private nextId = 1;

  /** Spawn a summon at the given position. Returns the instance. */
  spawn(config: SummonConfig, x: number, y: number): SummonInstance {
    const instanceId = `summon_${this.nextId++}`;
    const state = new SummonState(instanceId, config, x, y);
    this.summons.set(instanceId, state);

    this.events.push({
      type: 'spawned',
      instanceId,
      config,
      x,
      y,
    });

    return state;
  }

  /** Destroy a specific summon by instance ID. */
  destroy(instanceId: string): void {
    const state = this.summons.get(instanceId);
    if (!state) return;
    state.kill();
    this.events.push({
      type: 'destroyed',
      instanceId,
      config: state.config,
      x: state.x,
      y: state.y,
    });
    this.summons.delete(instanceId);
  }

  /** Destroy all active summons. */
  destroyAll(): void {
    for (const [id, state] of this.summons) {
      state.kill();
      this.events.push({
        type: 'destroyed',
        instanceId: id,
        config: state.config,
        x: state.x,
        y: state.y,
      });
    }
    this.summons.clear();
  }

  /** Get all living summons. */
  getAll(): readonly SummonInstance[] {
    return Array.from(this.summons.values()).filter(s => !s.isDead);
  }

  /** Get summons by archetype. */
  getByArchetype(archetype: SummonArchetype): readonly SummonInstance[] {
    return Array.from(this.summons.values())
      .filter(s => !s.isDead && s.config.archetype === archetype);
  }

  /** Get a specific summon by instance ID. */
  get(instanceId: string): SummonInstance | undefined {
    return this.summons.get(instanceId);
  }

  /** Count of living summons. */
  get count(): number {
    return Array.from(this.summons.values()).filter(s => !s.isDead).length;
  }

  /** Apply damage to a summon. Returns true if the summon died. */
  applyDamage(instanceId: string, amount: number): boolean {
    const state = this.summons.get(instanceId);
    if (!state || state.isDead) return false;
    state.takeDamage(amount);
    if (state.isDead) {
      this.events.push({
        type: 'died',
        instanceId,
        config: state.config,
        x: state.x,
        y: state.y,
      });
      this.summons.delete(instanceId);
      return true;
    }
    return false;
  }

  /**
   * Command: all summons converge on a target position.
   * Sets a convergence target that the integration layer reads
   * to override AI pathfinding.
   */
  commandConverge(targetX: number, targetY: number): { instanceId: string; targetX: number; targetY: number }[] {
    const commands: { instanceId: string; targetX: number; targetY: number }[] = [];
    for (const state of this.summons.values()) {
      if (state.isDead) continue;
      commands.push({ instanceId: state.instanceId, targetX, targetY });
    }
    return commands;
  }

  /**
   * Command: detonate all summons. Each summon dies and produces
   * a detonate result with damage and radius for the caller to apply.
   */
  commandDetonate(radiusMultiplier: number, baseDamage: number): DetonateResult[] {
    const results: DetonateResult[] = [];
    const TILE_SIZE = 32;
    for (const [id, state] of this.summons) {
      if (state.isDead) continue;
      results.push({
        instanceId: id,
        x: state.x,
        y: state.y,
        damage: baseDamage,
        radiusTiles: state.config.leashRadiusTiles * radiusMultiplier,
      });
      state.kill();
      this.events.push({
        type: 'detonated',
        instanceId: id,
        config: state.config,
        x: state.x,
        y: state.y,
      });
    }
    this.summons.clear();
    return results;
  }

  /**
   * Per-frame update: duration expiry.
   * AI tick and movement are handled by the integration layer.
   */
  update(delta: number): SummonEvent[] {
    this.events.length = 0;

    const expired: string[] = [];
    for (const [id, state] of this.summons) {
      if (state.isDead) continue;
      state.elapsedMs += delta;

      // Duration expiry
      if (state.config.durationMs && state.config.durationMs > 0) {
        if (state.elapsedMs >= state.config.durationMs) {
          state.kill();
          expired.push(id);
          this.events.push({
            type: 'expired',
            instanceId: id,
            config: state.config,
            x: state.x,
            y: state.y,
          });
        }
      }
    }

    for (const id of expired) {
      this.summons.delete(id);
    }

    return [...this.events];
  }

  /** Drain events since last call. Useful outside of update(). */
  drainEvents(): SummonEvent[] {
    const drained = [...this.events];
    this.events.length = 0;
    return drained;
  }
}
